import { useCallback, useEffect, useReducer, useRef, useState } from 'react';
import { authTokenManager } from 'modules/taboola-common-frontend-modules/authentication';
import { sanitizeConversionName } from 'services/api/trackingApi/trackingApiTransformer';
import { STEP_IDS } from '../config/stepIds';
import { useSaveBulkRules } from './useSaveBulkRules';

export const ERROR_TYPES = {
    GENERAL_ERROR: 'GENERAL_ERROR',
    DUPLICATE_NAME: 'DUPLICATE_NAME',
    SESSION_TOKEN_EXPIRED: 'SESSION_TOKEN_EXPIRED',
};

const errorModalReducer = (state, action) => {
    switch (action.type) {
        case 'SHOW_ERROR':
            return {
                show: true,
                errorType: action.payload.errorType,
                duplicateConversionName: action.payload.duplicateConversionName || '',
            };
        case 'HIDE_ERROR':
            return {
                ...state,
                show: false,
            };
        case 'RESET_ERROR':
            return {
                show: false,
                errorType: ERROR_TYPES.GENERAL_ERROR,
                duplicateConversionName: '',
            };
        default:
            return state;
    }
};

export const useSaveAndClose = ({ conversionsToSave, navigateToStepId, unfilteredFetchedConversions }) => {
    const [isLoading, setIsLoading] = useState(false);
    const [errorModalState, dispatchErrorModal] = useReducer(errorModalReducer, {
        show: false,
        errorType: ERROR_TYPES.GENERAL_ERROR,
        duplicateConversionName: '',
    });
    const startTimeRef = useRef(null);
    const timeoutRef = useRef(null);
    const saveCompletedRef = useRef(false);
    const MIN_LOADING_TIME = 1000;
    const isMounted = useRef(true);

    useEffect(() => {
        return () => {
            isMounted.current = false;
            clearTimeout(timeoutRef.current);
        };
    }, []);

    const showSuccessWithMinLoadingTime = useCallback(() => {
        saveCompletedRef.current = true;
        const elapsed = Date.now() - startTimeRef.current;

        const show = () => {
            if (!isMounted.current) {
                return;
            }
            setIsLoading(false);
            sessionStorage.clear();
            navigateToStepId(STEP_IDS.SUCCESS_TOAST);
        };

        const delay = Math.max(MIN_LOADING_TIME - elapsed, 0);
        setTimeout(show, delay);
    }, [navigateToStepId]);

    const showErrorWithMinLoadingTime = useCallback((type = ERROR_TYPES.GENERAL_ERROR, duplicateName = '') => {
        saveCompletedRef.current = true;
        const elapsed = Date.now() - startTimeRef.current;

        const show = () => {
            if (!isMounted.current) {
                return;
            }
            setIsLoading(false);
            dispatchErrorModal({
                type: 'SHOW_ERROR',
                payload: {
                    errorType: type,
                    duplicateConversionName: duplicateName,
                },
            });
        };

        const delay = Math.max(MIN_LOADING_TIME - elapsed, 0);
        setTimeout(show, delay);
    }, []);

    const saveBulkRules = useSaveBulkRules({
        onSuccess: showSuccessWithMinLoadingTime,
        onFailure: showErrorWithMinLoadingTime,
    });

    const checkUniqueConversionNames = useCallback(() => {
        const conversionNameOnServerToIdMap = new Map();
        for (const conversion of unfilteredFetchedConversions) {
            conversionNameOnServerToIdMap.set(conversion.conversionName, conversion.id);
        }
        for (const conversionToSave of conversionsToSave) {
            const sanitizedConversionName = sanitizeConversionName(
                conversionToSave.category,
                conversionToSave.conversionName
            );
            const idOnServer = conversionNameOnServerToIdMap.get(sanitizedConversionName);
            if (idOnServer && idOnServer !== conversionToSave.id) {
                return { duplicateName: conversionToSave.conversionName };
            }
        }

        const conversionToSaveNames = new Set();
        for (const conversionToSave of conversionsToSave) {
            const sanitizedConversionName = sanitizeConversionName(
                conversionToSave.category,
                conversionToSave.conversionName
            );
            if (conversionToSaveNames.has(sanitizedConversionName)) {
                return { duplicateName: conversionToSave.conversionName };
            }
            conversionToSaveNames.add(sanitizedConversionName);
        }

        return { duplicateName: undefined };
    }, [conversionsToSave, unfilteredFetchedConversions]);

    const handleSaveAndClose = useCallback(async () => {
        if (isLoading) {
            return;
        }

        if (authTokenManager.isExpired()) {
            dispatchErrorModal({
                type: 'SHOW_ERROR',
                payload: {
                    errorType: ERROR_TYPES.SESSION_TOKEN_EXPIRED,
                },
            });
            return;
        }

        const { duplicateName } = checkUniqueConversionNames();
        if (duplicateName) {
            dispatchErrorModal({
                type: 'SHOW_ERROR',
                payload: {
                    errorType: ERROR_TYPES.DUPLICATE_NAME,
                    duplicateConversionName: duplicateName,
                },
            });
            return;
        }

        setIsLoading(true);
        startTimeRef.current = Date.now();
        saveCompletedRef.current = false;

        if (conversionsToSave.length === 0) {
            showSuccessWithMinLoadingTime();
            return;
        }

        timeoutRef.current = setTimeout(() => {
            if (!saveCompletedRef.current) {
                if (!isMounted.current) {
                    return;
                }
                setIsLoading(false);
                dispatchErrorModal({
                    type: 'SHOW_ERROR',
                    payload: {
                        errorType: ERROR_TYPES.GENERAL_ERROR,
                    },
                });
            }
        }, 10000);

        try {
            const savePromise = saveBulkRules();
            const timeoutPromise = new Promise((_, reject) =>
                setTimeout(() => {
                    if (!saveCompletedRef.current) {
                        reject(new Error('Save operation timed out'));
                    }
                }, 12000)
            );

            await Promise.race([savePromise, timeoutPromise]);

            if (!saveCompletedRef.current) {
                showErrorWithMinLoadingTime(ERROR_TYPES.GENERAL_ERROR);
            }
        } catch (err) {
            if (!saveCompletedRef.current) {
                showErrorWithMinLoadingTime(ERROR_TYPES.GENERAL_ERROR);
            }
        }
    }, [
        isLoading,
        conversionsToSave,
        showSuccessWithMinLoadingTime,
        saveBulkRules,
        showErrorWithMinLoadingTime,
        checkUniqueConversionNames,
    ]);

    const hideErrorModal = useCallback(() => {
        dispatchErrorModal({ type: 'HIDE_ERROR' });
    }, []);

    return {
        isLoading,
        showErrorModal: errorModalState.show,
        errorType: errorModalState.errorType,
        duplicateConversionName: errorModalState.duplicateConversionName,
        handleSaveAndClose,
        hideErrorModal,
    };
};
